import React from 'react';
import { Tabs } from '@mui/material';
import { Routes } from '@app/constants/routes';
import { useMatchActiveRoute, useTypedNavigate } from '@app/router';
import { mutable } from '@app/utils/type.utils';
import { SettingWorkingDays } from '../pages/SettingWorkingDays';
import { SettingFindings } from '../pages/SettingFindings';
import { SettingsCurrency } from '../pages/SettingsCurrency';
import { OutlinedTab } from '@app/shared/ui-components/tabs/OutlinedTab';
import { useGetActionLogsTotal } from '@app/domain/action-log/api/hooks/action-log.api.hook';
import { ERole } from '@app/swagger-types';
import { AdminsLoggingPage } from '../nested/setting-logging/pages/AdminsLogging.page';
import { TestersLoggingPage } from '../nested/setting-logging/pages/TestersLogging.page';
import { ClientsLoggingPage } from '../nested/setting-logging/pages/ClientsLogging.page';

const allSettingsRoutes = mutable([
  Routes.settings.logging,
  Routes.settings.workingDays,
  Routes.settings.findings,
  Routes.settings.currency,
  Routes.settings.adminsLogging,
  Routes.settings.testersLogging,
  Routes.settings.clientsLogging,
] as const);

const loggingTabRoutes = mutable([
  Routes.settings.clientsLogging,
  Routes.settings.testersLogging,
  Routes.settings.adminsLogging,
] as const);

export const AllSettingsTabsWrapper: React.FC = () => {
  const navigate = useTypedNavigate();
  const activeRoute = useMatchActiveRoute(allSettingsRoutes);
  const activeLoggingRoute = useMatchActiveRoute(loggingTabRoutes);

  const clientsLogsTotal = useGetActionLogsTotal(ERole.ROLE_CLIENT);
  const testersLogsTotal = useGetActionLogsTotal(ERole.ROLE_TESTER);
  const adminsLogsTotal = useGetActionLogsTotal(ERole.ROLE_ADMIN);

  const isLoggingActive =
    activeRoute === Routes.settings.logging ||
    activeRoute === Routes.settings.adminsLogging ||
    activeRoute === Routes.settings.testersLogging ||
    activeRoute === Routes.settings.clientsLogging;
  const isWorkingDaysActive = activeRoute === Routes.settings.workingDays;
  const isFindingsActive = activeRoute === Routes.settings.findings;
  const isCurrencyActive = activeRoute === Routes.settings.currency;

  // Determine which logging tab is active, default to clients if on base /settings/logging
  const activeLoggingTab = activeLoggingRoute || Routes.settings.clientsLogging;
  const isClientsLoggingActive = activeLoggingTab === Routes.settings.clientsLogging;
  const isTestersLoggingActive = activeLoggingTab === Routes.settings.testersLogging;
  const isAdminsLoggingActive = activeLoggingTab === Routes.settings.adminsLogging;

  // Debug logging
  console.log('AllSettingsTabsWrapper Debug:', {
    activeRoute,
    activeLoggingRoute,
    activeLoggingTab,
    isClientsLoggingActive,
    isTestersLoggingActive,
    isAdminsLoggingActive,
  });

  const handleLoggingTabChange = (event: React.ChangeEvent<unknown>, to: typeof loggingTabRoutes[number]) => {
    navigate({ to });
  };

  return (
    <>
      <div className={isLoggingActive ? 'block' : 'hidden'}>
        <div>
          <Tabs
            value={activeLoggingTab}
            onChange={handleLoggingTabChange}
            aria-label="logging tabs"
            className="mb-8"
            TabIndicatorProps={{
              style: {
                display: 'none',
              },
            }}
          >
            <OutlinedTab label="Clients" value={loggingTabRoutes[0]} count={clientsLogsTotal} />
            <OutlinedTab label="Testers" value={loggingTabRoutes[1]} count={testersLogsTotal} />
            <OutlinedTab label="Admins" value={loggingTabRoutes[2]} count={adminsLogsTotal} />
          </Tabs>
        </div>
        <div>
          {isClientsLoggingActive && <ClientsLoggingPage />}
          {isTestersLoggingActive && <TestersLoggingPage />}
          {isAdminsLoggingActive && <AdminsLoggingPage />}
        </div>
      </div>
      <div className={isWorkingDaysActive ? 'block' : 'hidden'}>
        <SettingWorkingDays />
      </div>
      <div className={isFindingsActive ? 'block' : 'hidden'}>
        <SettingFindings />
      </div>
      <div className={isCurrencyActive ? 'block' : 'hidden'}>
        <SettingsCurrency />
      </div>
    </>
  );
};
